import { Executor, FromSchema, ZodDate } from '@40seas/entities/common';
import { AmlInfo, AmlInfoSchema } from './aml-info.entity';
import { Segment, SegmentEntity, WithSegmentBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import { IdentitySearchQuery } from './identity-search-query.type';
import { IdentityCandidate, IdentityCandidateSchema } from './identity-candidate.entity';

export const IdentityAdditionalDataSchema = z.object({
    entityStatus: z.string().nullish(),
    countryOfIncorporation: z.string().nullish(),
    registrationNumber: z.string().nullish(),
    declaredIndustry: z.string().nullish(),
    industryCodes: z
        .array(
            z.object({
                code: z.string(),
                description: z.string().optional(),
                codeType: z.string().optional(),
            }),
        )
        .nullish(),
    website: z.string().nullish(),

    bankruptcy: z
        .object({
            hasBankruptcy: z.boolean(),
            hasOpenBankruptcy: z.boolean().optional(),
            filings: z
                .array(
                    z.object({
                        type: z.string().nullish(),
                        filingDate: z.string().nullish(),
                        dischargeDate: z.string().nullish(),
                        status: z.string().nullish(),
                    }),
                )
                .default([]),
        })
        .nullish(),

    insolvency: z
        .object({
            hasInsolvency: z.boolean(),
            filings: z
                .array(
                    z.object({
                        startDate: z.string().nullish(),
                        practitionerName: z.string().nullish(),
                        status: z.string().nullish(),
                    }),
                )
                .default([]),
        })
        .nullish(),

    liquidation: z
        .object({
            hasLiquidation: z.boolean(),
            filings: z
                .array(
                    z.object({
                        startDate: z.string().nullish(),
                        status: z.string().nullish(),
                    }),
                )
                .default([]),
        })
        .nullish(),

    corporateCriminalRecord: z
        .object({
            hasRecord: z.boolean(),
            hasCriminalProceedings: z.boolean().optional(),
            hasOpenCriminalProceedings: z.boolean().optional(),
            hasSuits: z.boolean().optional(),
            hasDebarments: z.boolean().optional(),
            details: z.string().nullish(),
            source: z.string().nullish(),
        })
        .nullish(),

    entitySanctions: z
        .object({
            hasSanctions: z.boolean(),
            sanctionListNames: z.array(z.string()).default([]),
            hitCount: z.number().optional(),
        })
        .nullish(),

    linkedInExists: z.boolean().nullish(),
    linkedInUrl: z.string().nullish(),
    googleReviewsExist: z.boolean().nullish(),
    googleReviewsData: z
        .object({
            reviewCount: z.number().nullish(),
            averageRating: z.number().nullish(),
        })
        .nullish(),

    enrichedAt: z.string().nullish(),
    enrichmentSources: z.array(z.string()).nullish(),
});

export type IdentityAdditionalData = z.infer<typeof IdentityAdditionalDataSchema>;

export const IdentitySchema = WithSegmentBaseEntitySchema(
    z
        .object({
            buyer: z.string().openapi({ description: 'Reference to the buyer entity this identity belongs to' }),
            searchQuery: z.any().optional().openapi({ description: 'Original search query used to find this identity' }),
            legalName: z.string().optional().openapi({ description: 'Legal name of the business entity' }),
            address: z.any().optional().openapi({ description: 'Physical address of the business' }),
            registrationNumber: z
                .string()
                .optional()
                .openapi({ description: 'Official business registration number (e.g., Companies House number)' }),
            dunsNumber: z.string().optional().openapi({ description: 'Dun & Bradstreet Universal Numbering System identifier' }),
            vatNumber: z.string().optional().openapi({ description: 'VAT/Tax identification number for this business' }),
            businessStartDate: ZodDate.optional().openapi({
                format: 'date-time',
                type: 'string',
                description: 'Date when the business started operations',
            }),
            emailAddresses: z.array(z.string()).optional().openapi({ description: 'Email addresses associated with this business' }),
            amlInfo: AmlInfoSchema.optional().openapi({ description: 'Anti-Money Laundering information for this business' }),
            website: z.string().optional().openapi({ description: 'Company website URL' }),
            businessType: z.string().optional().openapi({ description: 'Type or category of business (e.g., Ltd, PLC, Sole Trader)' }),
            dataSources: z
                .array(z.string())
                .optional()
                .openapi({ description: 'List of external data sources used to verify this identity (e.g., Companies House, D&B)' }),
            lastVerificationDate: z
                .date()
                .optional()
                .openapi({ format: 'date-time', type: 'string', description: 'Date when the identity was last verified' }),

            additionalData: IdentityAdditionalDataSchema.nullish().openapi({
                description: 'Enrichment-gathered data stored for re-use across assessment cycles',
            }),

            /** All candidates found from providers (after deduplication) */
            candidates: z
                .array(IdentityCandidateSchema)
                .optional()
                .openapi({ description: 'All candidates found from providers after deduplication' }),
        })
        .openapi({ description: 'Business identity information aggregated from various data sources for verification purposes' }),
).strict();

export class Identity extends FromSchema(IdentitySchema) implements SegmentEntity {
    id: string;
    buyer: string;
    searchQuery?: IdentitySearchQuery;
    legalName?: string;
    address?: any;
    registrationNumber?: string;
    dunsNumber?: string;
    vatNumber?: string;
    businessStartDate?: Date;
    emailAddresses?: string[];
    amlInfo?: AmlInfo;
    website?: string;
    businessType?: string;
    dataSources?: string[];
    lastVerificationDate?: Date;

    additionalData?: IdentityAdditionalData | null;

    // Candidates found from providers (after deduplication)
    candidates?: IdentityCandidate[];

    tenantId: string;
    seller?: string;
    createdAt?: Date;
    updatedAt?: Date;
    createdBy?: Executor;
    segments?: Segment[];
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}
